import { useState, useEffect } from 'react';
import axios from 'axios';
import { buildApiUrl } from '../apiBase';
import { parseLocalDate } from '../utils/timeUtils';

// Shared by WeeklyPriceChart, WeeklyScoreChart, WeeklyMacdChart and
// WeeklyRsiChart on StockDetail's weekly tab. Previously each chart fetched
// its own subset of these three payloads independently -- weekly-indicators
// 4x, weekly-scores 3x, weekly-price-history 2x (11 requests for 5 distinct
// payloads across the whole page). See "StockDetail fires 11 requests for 5
// payloads" (Tech Debt 2026-09).
export function useWeeklySeries(symbol, { limit = 250, enabled = true } = {}) {
  const [priceHistory, setPriceHistory] = useState(null);
  const [indicators, setIndicators] = useState(null);
  const [scores, setScores] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!symbol || !enabled) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const sortByDate = (rows) => rows.slice().sort((a, b) => parseLocalDate(a.date) - parseLocalDate(b.date));

    Promise.all([
      axios.get(buildApiUrl(`/stocks/${symbol}/weekly-price-history?limit=${limit}`)),
      axios.get(buildApiUrl(`/stocks/${symbol}/weekly-indicators?limit=${limit}`)),
      axios.get(buildApiUrl(`/stocks/${symbol}/weekly-scores?limit=${limit}`)).catch(() => ({ data: {} })),
    ])
      .then(([priceRes, indRes, scoreRes]) => {
        if (cancelled) return;
        setPriceHistory(sortByDate(priceRes.data?.weekly_price_history || []));
        setIndicators(sortByDate(indRes.data?.indicators || []));
        setScores(sortByDate(scoreRes.data?.scores || []));
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message);
        setPriceHistory([]);
        setIndicators([]);
        setScores([]);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [symbol, limit, enabled]);

  return { priceHistory, indicators, scores, loading, error };
}
